import { FilterFunnel01Icon, SearchRefractionIcon, XIcon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import { RadioGroup, RadioGroupItem } from "@/kit/ui/radio-group";
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuTrigger,
} from "@/kit/ui/responsive-menu";
import { LocationKind } from "./__generated__/EventFiltersTotalCountFragmentPaginationQuery.graphql";
import { assertNever } from "@/utils/helpers";
import {
  useDebounced,
  useFilterFormSync,
  useOnChangeEffect,
} from "@/utils/hooks";
import { preprocessEdit } from "@/utils/relay";
import { cn } from "@/utils/tailwind";
import { useValibotForm, useValibotFormContext } from "@/utils/validation";
import { MouseEventHandler, useState, useTransition } from "react";
import { SubmitHandler } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import { RewardKind } from "../__generated__/RewardFragment.graphql";
import { TagsField } from "../DatasetFilters/TagsField";
import { EventFiltersFragment$key } from "./__generated__/EventFiltersFragment.graphql";
import { EventFiltersTotalCountFragment$key } from "./__generated__/EventFiltersTotalCountFragment.graphql";
import {
  DEFAULT_EVENT_QUERY_FILTERS,
  LOCATIONS,
  REWARDS,
  Schema,
  schema,
  SchemaInput,
} from "./schema";

const EventFiltersTotalCountFragment = graphql`
  fragment EventFiltersTotalCountFragment on Query
  @refetchable(queryName: "EventFiltersTotalCountFragmentPaginationQuery")
  @argumentDefinitions(filters: { type: "EventQueryFilters" }) {
    eventCount: events(first: 0, filters: $filters) {
      totalCount
    }
  }
`;

const EventFiltersFragment = graphql`
  fragment EventFiltersFragment on Query
  @argumentDefinitions(filters: { type: "EventQueryFilters" }) {
    ...TagsFieldsFragment @arguments(activityKind: EVENT)
    ...EventFiltersTotalCountFragment @arguments(filters: $filters)
  }
`;

export type ChangeFiltersFn = SubmitHandler<SchemaInput>;

export interface EventFiltersProps {
  query: EventFiltersFragment$key;
  filters: SchemaInput;
  onChangeFilters: ChangeFiltersFn;
  withFilters?: boolean;
}

export default function EventFilters({
  withFilters = false,
  ...props
}: EventFiltersProps) {
  const query = useFragment(EventFiltersFragment, props.query);
  const intl = useIntl();
  const form = useValibotForm(schema, {
    defaultValues: {
      ...props.filters,
      search: props.filters.search?.trim() ?? "",
    },
  });
  const onSubmitSearch = useFilterFormSync(form, {
    ...props.filters,
    search: props.filters.search?.trim() ?? "",
  });
  const [open, setOpen] = useState(false);

  const search = form.watch("search");
  const _search = useDebounced(search, 400, { leading: false, trailing: true });

  const doSubmit = form.handleSubmit(({ search, tags, rewards, location }) => {
    onSubmitSearch(search);
    props.onChangeFilters(
      preprocessEdit(
        {
          search: search === "" ? undefined : search,
          tags: tags?.length === 0 ? undefined : tags,
          rewards: rewards?.length === 0 ? undefined : rewards,
          locations: location ? [location] : undefined,
        },
        props.filters,
      ),
    );
    setOpen(false);
  });

  useOnChangeEffect(_search, () => doSubmit());

  const doReset: MouseEventHandler = (event) => {
    event.preventDefault();
    form.reset(DEFAULT_EVENT_QUERY_FILTERS);
    onSubmitSearch(undefined);
    props.onChangeFilters(DEFAULT_EVENT_QUERY_FILTERS);
    setOpen(false);
  };

  const appliedFilterCount = appliedFilters(props.filters);
  return (
    <Form form={form} onSubmit={doSubmit} className="w-full flex gap-4">
      <FormField
        control={form.control}
        name="search"
        render={({ field: { value, ...field } }) => (
          <FormItem className="flex-1">
            <FormControl>
              <Input
                {...field}
                value={value ?? ""}
                startDecorator={
                  <SearchRefractionIcon className="w-5 h-5 text-grey-500" />
                }
                endDecorator={
                  search?.trim() && (
                    <XIcon
                      role="button"
                      className="text-grey-500"
                      onClick={(event) => {
                        event.preventDefault();
                        form.setValue("search", "");
                        onSubmitSearch(undefined);
                        props.onChangeFilters({ search: undefined });
                      }}
                    />
                  )
                }
                placeholder={intl.formatMessage({
                  defaultMessage: "Search",
                })}
              />
            </FormControl>
          </FormItem>
        )}
      />

      {withFilters && (
        <ResponsiveMenu open={open} onOpenChange={setOpen}>
          <ResponsiveMenuTrigger asChild>
            <Button
              className={cn(appliedFilterCount >= 1 && "relative")}
              variant="secondary"
              size="md"
            >
              <FilterFunnel01Icon className="w-5 h-5 text-grey-700" />
              <p className="md:block hidden">
                <FormattedMessage defaultMessage="Filters" />
              </p>
              {appliedFilterCount >= 1 && (
                <span className="absolute -top-2 -right-1">
                  <Badge variant="info">{appliedFilterCount}</Badge>
                </span>
              )}
            </Button>
          </ResponsiveMenuTrigger>

          <ResponsiveMenuContent
            className="p-0 lg:min-w-(--radix-popper-anchor-width) lg:w-96"
            align="end"
          >
            <div className="flex flex-col p-4 gap-4">
              <TagsField query={query} />
              <hr className="text-grey-100 my-2" />
              <FormField
                control={form.control}
                name="rewards"
                render={({ field }) => {
                  const selected = field.value ?? [];
                  const selectedSet = new Set(selected);
                  const toggleReward = (reward: RewardKind) => {
                    const current = field.value ?? [];

                    const next = current.includes(reward)
                      ? current.filter(
                          (current_reward) => current_reward !== reward,
                        )
                      : [...current, reward];

                    field.onChange(next);
                  };
                  return (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-grey-600">
                        <FormattedMessage defaultMessage="Prize" />
                      </FormLabel>
                      <FormControl className="pt-4">
                        <div className="flex flex-wrap gap-2 overflow-y-auto">
                          {REWARDS.map((reward) => {
                            const isActive = selectedSet.has(reward);

                            return (
                              <Badge
                                key={reward}
                                variant={isActive ? "secondary" : undefined}
                                className="hover:cursor-pointer select-none"
                                onClick={(event) => {
                                  event.preventDefault();
                                  toggleReward(reward);
                                }}
                              >
                                <RewardLabel kind={reward} />
                                {isActive && (
                                  <span
                                    role="button"
                                    className="hover:cursor-pointer"
                                  >
                                    <XIcon
                                      className="w-4 h-4"
                                      onClick={() => toggleReward(reward)}
                                    />
                                  </span>
                                )}
                              </Badge>
                            );
                          })}
                        </div>
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
              <hr className="text-grey-100 my-2" />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-grey-600">
                        <FormattedMessage defaultMessage="Location type" />
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value?.toString()}
                        >
                          {LOCATIONS.map((location) => (
                            <FormItem
                              key={location}
                              className="space-y-0 flex items-center gap-3"
                            >
                              <FormControl>
                                <RadioGroupItem value={location} />
                              </FormControl>
                              <FormLabel>
                                <LocationLabel kind={location} />
                              </FormLabel>
                            </FormItem>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            </div>

            <hr className="text-grey-100" />
            <div className="w-full flex flex-row justify-between items-center gap-4 p-4">
              <Button variant="secondary" onClick={doReset} className="w-full">
                <FormattedMessage defaultMessage="Clear all" />
              </Button>
              <SubmitButton query={query} onSubmit={doSubmit} />
            </div>
          </ResponsiveMenuContent>
        </ResponsiveMenu>
      )}
    </Form>
  );
}

function RewardLabel({ kind }: { kind: RewardKind }) {
  switch (kind) {
    case "BADGE":
      return <FormattedMessage defaultMessage="Badge" />;
    case "LEARNING":
      return <FormattedMessage defaultMessage="Learning" />;
    case "MONEY":
      return <FormattedMessage defaultMessage="Money reward" />;
    case "SWAG":
      return <FormattedMessage defaultMessage="Swag" />;
    default:
      assertNever(kind);
  }
}

function LocationLabel({ kind }: { kind: LocationKind }) {
  switch (kind) {
    case "HYBRID":
      return <FormattedMessage defaultMessage="Hybrid" />;
    case "IN_PERSON":
      return <FormattedMessage defaultMessage="In person" />;
    case "ONLINE":
      return <FormattedMessage defaultMessage="Online" />;
    default:
      assertNever(kind);
  }
}

interface SubmitButtonProps {
  query: EventFiltersTotalCountFragment$key;
  onSubmit: MouseEventHandler;
}

function SubmitButton(props: SubmitButtonProps) {
  const form = useValibotFormContext<Schema>();
  const [_, startTransition] = useTransition();
  const watchedFields = form.watch();
  const [
    {
      eventCount: { totalCount: count },
    },
    refetch,
  ] = useRefetchableFragment(EventFiltersTotalCountFragment, props.query);

  useOnChangeEffect(watchedFields, ({ search, tags, rewards, location }) => {
    startTransition(() => {
      refetch(
        {
          filters: {
            search: search === "" ? undefined : search,
            tags: tags?.length === 0 ? undefined : tags,
            rewards: rewards?.length === 0 ? undefined : rewards,
            locations: location ? [location] : undefined,
          },
        },
        {
          fetchPolicy: "store-or-network",
        },
      );
    });
  });

  return (
    <Button type="submit" onClick={props.onSubmit} className="w-full">
      <FormattedMessage
        defaultMessage="Show {count} results"
        values={{
          count,
        }}
      />
    </Button>
  );
}

function appliedFilters(filters: SchemaInput): number {
  const hasTags = !!filters.tags?.length;
  const hasRewards = !!filters.rewards?.length;
  const hasLocation = !!filters.location;
  return [hasTags, hasRewards, hasLocation].filter(Boolean).length;
}
